/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { D, ROUTES, GMAPS, LD_COLORS } from '@/data/dashboardData';
import { LD_NAMES } from '@/lib/computeStats';
import { BenchStat, Breadcrumb } from '@/components/shared';
import { ViewHeader } from '@/components/ViewHeader';
import { listCandidates } from '@/api/candidates';
import {
  hasUnresolvedSlate,
  mergeSlateNames,
  slateNamesFor,
} from '@/lib/slateNames';
import type { CandidateListItem } from '@/types/candidate';

/**
 * `{slate_names}` resolution for the scripts reference page.
 *
 * ⚠️ Scope note, so the next reader doesn't mistake this for more than it is:
 * `ScriptsView` is a **hardcoded mock** reading `D.scripts` from the static
 * `dashboardData.ts` blob. There is no Script model, no API, nothing
 * persisted — and building one is explicitly out of scope (plan §7: "A real
 * `Script` model is out of scope... building it is its own project, not a
 * rider on this one"). The single thing this plan owes the view is that
 * `"Hi, I'm with [Campaign]"` becomes `"Hi, I'm with {slate_names}"` and
 * ACTUALLY RESOLVES.
 *
 * Which slate? This page has no route context — it's a county-wide reference,
 * not a walk — so there is no route-derived slate to use. The honest answer at
 * this altitude is the tenant's **active candidates**, which is what the org is
 * canvassing for in aggregate. A route-specific opener is what the *briefing*
 * renders (plan §8.2 tier 1), and that one does derive from the route.
 */
function useSlateNames(): { slateNames: string; loading: boolean } {
  const [candidates, setCandidates] = useState<CandidateListItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listCandidates(true)
      .then((rows) => {
        if (!cancelled) setCandidates(rows);
      })
      .catch(() => {
        // Never block the scripts on a candidate fetch: the segment copy,
        // issues, and bridge lines are all still useful. An unresolved
        // `{slate_names}` token surfaces the failure on its own (below).
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    slateNames: candidates ? slateNamesFor(candidates) : '',
    loading: candidates === null,
  };
}

/**
 * One line of script copy with `{slate_names}` merged in.
 *
 * When the token can't be resolved it is rendered as a highlighted chip rather
 * than quietly left as literal text — the volunteer reading this page has to
 * SEE that the sentence is incomplete, not squint at braces and assume it's
 * fine.
 */
const ScriptLine: React.FC<{ copy: string; slateNames: string }> = ({
  copy,
  slateNames,
}) => {
  const merged = mergeSlateNames(copy || '', slateNames);
  if (!hasUnresolvedSlate(merged)) {
    return <p className="text-fg-secondary text-sm italic">"{merged}"</p>;
  }
  const [before, after] = merged.split('{slate_names}');
  return (
    <p className="text-fg-secondary text-sm italic">
      "{before}
      <span
        className="not-italic font-mono text-xs bg-status-warning-bg/40 text-status-warning border border-status-warning/50 rounded px-1 py-0.5"
        title="No active candidates — this merge field could not be resolved."
      >
        {'{slate_names}'}
      </span>
      {after}"
    </p>
  );
};

export const ScriptsView: React.FC<any> = ({ onBack }) => {
  const scripts = D.scripts;
  const { slateNames, loading } = useSlateNames();

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "County Map", action: "map" }, { label: "Door Scripts", action: null }]} onNavigate={() => null} />

      <ViewHeader title="Door Scripts" tagline="Voter segment-specific opening statements and talking points" />

      {/*
        The loud unresolved-merge banner. If no active candidate exists, every
        opener below still literally reads "Hi, I'm with {slate_names}" — and
        that is DELIBERATE (see lib/slateNames.ts). Swapping in a neutral
        phrase would read fine and be a lie; the volunteer would then say
        something generic at the door and nobody would ever notice the slate
        was never configured. An unresolved token plus this banner is the
        honest failure.
      */}
      {!loading && !slateNames && (
        <div className="rounded-lg border border-status-warning bg-status-warning-bg/30 p-4 text-status-warning">
          <div className="font-semibold">
            ⚠ No active candidates — these scripts are unfinished
          </div>
          <div className="text-sm mt-1 text-status-warning/90">
            The openers below still show the{' '}
            <code className="font-mono text-xs">{'{slate_names}'}</code>{' '}
            placeholder because nobody has been added to the slate yet. Add
            candidates in the Candidates admin screen and they will resolve
            here automatically. Don't send volunteers out with this.
          </div>
        </div>
      )}

      {!loading && slateNames && (
        <div className="rounded-lg border border-border-base bg-bg-raised p-3 text-sm text-fg-secondary">
          <span className="text-xs uppercase tracking-wide text-fg-muted mr-2">
            Knocking for
          </span>
          <strong className="text-fg-primary">{slateNames}</strong>
          <span className="text-fg-muted">
            {' '}— resolved into every opener below.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(scripts).map(([key, script]) => (
          <div key={key} className="bg-bg-raised rounded-xl p-6 border border-border-base">
            <div className="text-lg font-bold text-fg-primary mb-1">{key.replace(/_/g, " ").toUpperCase()}</div>
            <div className="text-xs text-fg-muted mb-4 font-semibold">{script.profile}</div>

            <div className="space-y-4">
              <div>
                <div className="text-sm font-semibold text-fg-secondary mb-2">Opener</div>
                {/* The line that started this. `[Campaign]` is gone; the merge
                    resolves to the real slate, or stays visibly unresolved. */}
                <ScriptLine copy={script.opener} slateNames={slateNames} />
              </div>

              <div>
                <div className="text-sm font-semibold text-fg-secondary mb-2">Bridge</div>
                <ScriptLine copy={script.bridge} slateNames={slateNames} />
              </div>

              <div>
                <div className="text-sm font-semibold text-fg-secondary mb-2">Ask</div>
                <ScriptLine copy={script.ask} slateNames={slateNames} />
              </div>

              <div>
                <div className="text-sm font-semibold text-fg-secondary mb-2">Key Issues</div>
                <ul className="space-y-1">
                  {script.issues.map((issue, i) => (
                    <li key={i} className="text-fg-secondary text-sm">• {issue}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};
